import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Typography from '../Typography';
import Button from '../Button/Button';
import Drawer from './Drawer';
import { theme } from '../../theme';
import CloseIcon from '../Icons/CloseIcon';
import { toast } from 'react-toastify';
import axiosClient from '../../api/axiosClient';
import RequestCancellationModal from '../Modal/RequestCancellationModal';
import FreeCancellationModal from '../Modal/FreeCancellationModal';
import Tag from '../Tag';
import { useAttendeeStateTranslations } from '../../constants/attendeeStates';
import { TICKET_FREE, TICKET_PAID } from '../../constants/eventTypes';
import { formatEventDateDisplay, formatShortDateTime } from '../../utils/dateUtils';

const EnrollmentDetailsDrawer = ({
  isOpen,
  onClose,
  enrollment,
  event,
  onRefetch,
  anchor = 'right',
  width
}) => {
  const { getStateLabel } = useAttendeeStateTranslations();
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('enrollmentDetails');
  const [showRequestCancellationModal, setShowRequestCancellationModal] = useState(false);
  const [showFreeCancellationModal, setShowFreeCancellationModal] = useState(false);
  const [isRequestingCancellation, setIsRequestingCancellation] = useState(false);
  const [isCancellingFree, setIsCancellingFree] = useState(false);



  if (!enrollment || !event) return null;

  const ticket = enrollment.ticketId;
  if (!ticket) {
    return (
      <Drawer anchor={anchor} open={isOpen} onClose={onClose} width={width}>
        <DrawerContainer>
          <DrawerHeader>
            <Typography as="h2" $variant="body-1-semibold">
              {t('title')}
            </Typography>
            <Button $iconOnly={true} onClick={onClose} $variant="ghost">
              <CloseIcon fill={theme.colors.defaultMain} width={20} height={20} />
            </Button>
          </DrawerHeader>
          <DrawerBody>
            <Typography as="p" $variant="body-1-regular">
              {t('errors.ticketNotFound')}
            </Typography>
          </DrawerBody>
        </DrawerContainer>
      </Drawer>
    );
  }

  const { status } = enrollment;

  const redirectToEvent = () => {
    onClose();
    if (event.slug) {
      navigate(`/events/${event.id}/${event.slug}`);
    } else {
      navigate(`/events/${event.id}/${event.title.replace(/\s+/g, '-').toLowerCase()}`);
    }
  };

  const cancelFree = async (message = '') => {
    setIsCancellingFree(true);
    try {
      const payload = message.trim() ? { userMessage: message.trim() } : {};
      await axiosClient.post(`/api/events/${event.id}/attendees/cancel`, payload);

      toast.success(t('toastMessages.enrollmentCancelled'));

      // Cerrar modales
      setShowFreeCancellationModal(false);

      // Cerrar el drawer inmediatamente para forzar un re-render completo
      onClose();

      // Agregar un pequeño retraso para asegurar que el backend haya procesado completamente
      setTimeout(async () => {
        await onRefetch();
        // Forzar un re-render adicional
        window.dispatchEvent(new Event('enrollment-updated'));
      }, 500);
    } catch (err) {
      console.error('❌ Error al cancelar:', err);
      toast.error(err.response?.data?.message || t('toastMessages.cancellationError'));
    } finally {
      setIsCancellingFree(false);
    }
  };

  const requestPaidCancel = async (message) => {
    setIsRequestingCancellation(true);
    try {
      await axiosClient.post(
        `/api/events/${event.id}/attendees/${enrollment._id}/request-cancel`,
        { userMessage: message }
      );

      toast.info(t('toastMessages.cancellationRequestSent'));
      setShowRequestCancellationModal(false);

      // Cerrar el drawer inmediatamente para forzar un re-render completo
      onClose();

      // Agregar un pequeño retraso para asegurar que el backend haya procesado completamente
      setTimeout(async () => {
        await onRefetch();
        // Forzar un re-render adicional
        window.dispatchEvent(new Event('enrollment-updated'));
      }, 500);
    } catch (err) {
      console.error('❌ Error en solicitud de cancelación:', err);
      toast.error(err.response?.data?.message || t('toastMessages.cancellationRequestError'));
    } finally {
      setIsRequestingCancellation(false);
    }
  };

  const handleCancel = () => {
    if (!ticket) return;
    if (ticket.type === TICKET_PAID) {
      setShowRequestCancellationModal(true);
    } else if (ticket.approvalRequired && status === 'confirmation_pending') {
      toast.error(t('toastMessages.cannotCancelPending'));
    } else {
      setShowFreeCancellationModal(true);
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'confirmation_pending':
        return t('statusMessages.confirmationPending');
      case 'cancellation_pending':
        return t('statusMessages.cancellationPending');
      case 'attending':
        return enrollment.cancellationDenied ? t('statusMessages.attendingDenied') : t('statusMessages.attendingConfirmed');
      case 'cancelled_by_user':
        return t('statusMessages.cancelledByUser');
      case 'cancelled_by_organizer':
        return t('statusMessages.cancelledByOrganizer');
      case 'rejected_by_organizer':
        return t('statusMessages.rejectedByOrganizer');
      case 'refund_accepted':
        return t('statusMessages.refundAccepted');
      case 'refunded':
        return t('statusMessages.refunded');
      case 'not_attending':
        return t('statusMessages.notAttending');
      default:
        return t('statusMessages.unknownStatus');
    }
  };



  return (
    <>
      <Drawer anchor={anchor} open={isOpen} onClose={onClose} width={width}>
        <DrawerContainer>
          <DrawerHeader>
            <Typography as="h2" $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold">
              {t('title')}
            </Typography>
            <Button size="l" sizeTablet="l" sizeMobile="m" $iconOnly={true} onClick={onClose} $variant="ghost">
              <CloseIcon fill={theme.colors.defaultMain} width={20} height={20} />
            </Button>
          </DrawerHeader>

          <DrawerBody>
            {/* Información del evento */}
            <Section>
              <EventInfo>
                {event.image && (
                  <EventImage src={event.image} alt={event.title} />
                )}
                <EventDetails>
                  <Typography as="h3" $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold">
                    {event.title}
                  </Typography>
                  <Typography as="p" $variant="body-3-regular" color={theme.colors.defaultWeak}>
                    {formatEventDateDisplay(event.startDate, event.endDate, i18n.language)}
                  </Typography>
                  <Typography as="p" $variant="body-3-regular" color={theme.colors.defaultWeak}>
                    {event.location}
                  </Typography>
                </EventDetails>
              </EventInfo>
            </Section>

            {/* Detalles */}
            <Section>
              <SectionWrapper>
                <SectionHeader>
                  <Typography as="h3" $variant="body-2-semibold" color={theme.colors.defaultStrong}>
                    {t('labels.details')}
                  </Typography>
                </SectionHeader>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>
                      <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultWeak}>
                        {t('labels.enrollmentDate')}:
                      </Typography>
                    </InfoLabel>
                    <InfoValue>
                      <Typography as="span" $variant="body-2-medium">
                        {formatShortDateTime(enrollment.createdAt, i18n.language, {
                          dateStyle: 'numeric',
                          includeComma: true,
                        })}
                      </Typography>
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>
                      <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultWeak}>
                        {t('labels.ticket')}:
                      </Typography>
                    </InfoLabel>
                    <InfoValue>
                      <Typography as="span" $variant="body-2-medium">
                        {ticket.name} ({ticket.type === TICKET_FREE ? t('labels.free') : `${ticket.price}€`})
                      </Typography>
                    </InfoValue>
                  </InfoItem>
                  {event.needsVehicle && enrollment.vehicleId && (
                    <InfoItem>
                      <InfoLabel>
                        <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultWeak}>
                          {t('labels.vehicle')}:
                        </Typography>
                      </InfoLabel>
                      <InfoValue>
                        <Typography as="span" $variant="body-2-medium">
                          {enrollment.vehicleId.brand} {enrollment.vehicleId.model}
                        </Typography>
                      </InfoValue>
                    </InfoItem>
                  )}
                </InfoGrid>
              </SectionWrapper>
            </Section>

            {/* Estado de la inscripción */}
            <Section>
              <SectionWrapper>
                <SectionHeader>
                  <Typography as="h3" $variant="body-2-semibold" color={theme.colors.defaultStrong}>
                    {t('labels.enrollmentStatus')}
                  </Typography>
                </SectionHeader>
                <StatusMessage>
                  <Tag $variant='warning' >{getStateLabel(status)}</Tag>
                  <Typography as="p" $variant="body-2-regular">
                    {getStatusMessage()}
                  </Typography>
                </StatusMessage>
              </SectionWrapper>
            </Section>

            {/* Sección de mensajes si existe userMessage o organizerMessage */}
            {(enrollment.userMessage || enrollment.organizerMessage) && (
              <Section>
                <SectionWrapper>
                  <SectionHeader>
                    <Typography as="h3" $variant="body-2-semibold" color={theme.colors.defaultStrong}>
                      {t('labels.messages')}
                    </Typography>
                  </SectionHeader>

                  {/* Mensaje del usuario si existe */}
                  {enrollment.userMessage && (
                    <Message>
                      {/* <Decoration /> */}
                      <MessageHeader>
                        <Typography as="h3" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                          {enrollment.userId?.name && enrollment.userId?.lastName
                            ? `${enrollment.userId.name} ${enrollment.userId.lastName}`
                            : enrollment.userId?.name || 'Usuario'}
                        </Typography>
                        <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultWeak}>
                          {t('labels.requestFor')} {ticket.type === TICKET_PAID ? t('labels.refund') : t('labels.cancellation')}
                        </Typography>
                      </MessageHeader>
                      <MessageContainer>
                        <Typography as="p" $variant="body-2-regular" color={theme.colors.defaultStrong}>
                          {enrollment.userMessage}
                        </Typography>
                      </MessageContainer>
                    </Message>
                  )}

                  {/* Mensaje del organizador si existe */}
                  {enrollment.organizerMessage && (
                      <Message>
                        {/* <Decoration /> */}
                        <MessageHeader>
                          <Typography as="h3" $variant="body-2-medium" color={theme.colors.defaultStrong}>
                            {event.owner?.name && event.owner?.lastName
                              ? `${event.owner.name} ${event.owner.lastName}`
                              : event.owner?.name || t('labels.organizer')}
                          </Typography>
                          <Typography as="span" $variant="body-3-medium" color={theme.colors.defaultWeak}>
                            {status === 'refunded' ? t('labels.refundProcessed') :
                              status === 'refund_accepted' ? t('labels.refundAccepted') :
                                status === 'rejected_by_organizer' ? t('labels.registrationRejected') :
                                enrollment.cancellationDenied ? t('labels.cancellationDenied') : t('labels.response')}
                          </Typography>
                        </MessageHeader>
                        <MessageContainer>
                          <Typography as="p" $variant="body-2-regular" color={theme.colors.defaultStrong}>
                            "{enrollment.organizerMessage}"
                          </Typography>
                        </MessageContainer>
                      </Message>
                    )}

                  {/* <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    
                  </div> */}
                </SectionWrapper>
              </Section>
            )}
          </DrawerBody>

          {/* Footer con acciones */}
          {((status === 'attending' || status === 'confirmation_pending') && status !== 'refund_accepted' && status !== 'refunded' && status !== 'cancelled_by_organizer' && status !== 'rejected_by_organizer') ||
            ((status === 'not_attending' || status === 'cancelled_by_organizer') && ticket.type === TICKET_FREE) ? (
            <DrawerFooter>
              <ActionsContainer>
                {/* Botón de acción según el estado */}
                {(status === 'attending' || status === 'confirmation_pending') && status !== 'refund_accepted' && status !== 'refunded' && status !== 'cancelled_by_organizer' && status !== 'rejected_by_organizer' && (
                  <Button
                    onClick={handleCancel}
                    disabled={
                      (ticket.type === TICKET_FREE && ticket.approvalRequired && status === 'confirmation_pending') ||
                      status === 'cancellation_pending'
                    }
                    $variant="outlineDanger"
                    size="l"
                    sizeTablet="l"
                    sizeMobile="m"
                    style={{ flexGrow: 1 }}
                  >
                    {ticket.type === TICKET_PAID ? t('buttons.requestCancellation') : t('buttons.cancelEnrollment')}
                  </Button>
                )}

                {/* Botón para volver a inscribirse si es posible */}
                {(status === 'not_attending' || status === 'cancelled_by_organizer') && ticket.type === TICKET_FREE && (
                  <Button
                    onClick={redirectToEvent}
                    $variant="outline" style={{ flexGrow: 1 }}
                    size="l"
                    sizeTablet="l"
                    sizeMobile="m"
                  >
                    {t('buttons.enrollAgain')}
                  </Button>
                )}
              </ActionsContainer>
            </DrawerFooter>
          ) : null}
        </DrawerContainer>
      </Drawer>

      {showRequestCancellationModal && (
        <RequestCancellationModal
          isOpen={showRequestCancellationModal}
          onClose={() => setShowRequestCancellationModal(false)}
          onConfirm={requestPaidCancel}
          isLoading={isRequestingCancellation}
          ticketType={ticket.type}
          t={t}
        />
      )}

      {showFreeCancellationModal && (
        <FreeCancellationModal
          isOpen={showFreeCancellationModal}
          onClose={() => setShowFreeCancellationModal(false)}
          onConfirm={cancelFree}
          isLoading={isCancellingFree}
        />
      )}
    </>
  );
};

EnrollmentDetailsDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  enrollment: PropTypes.object,
  event: PropTypes.object,
  onRefetch: PropTypes.func,
  anchor: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default EnrollmentDetailsDrawer;

// Styled Components
const DrawerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background-color: ${({ theme }) => theme.fill.defaultMain};
`;

const DrawerBody = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.sizing.sm};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: ${({ theme }) => theme.sizing.xs} 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const StatusMessage = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.sm} 12px;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.xs} 12px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const InfoLabel = styled.div`
  flex-shrink: 0;
  min-width: 120px;
`;

const InfoValue = styled.div`
  flex: 1;
  text-align: right;
`;

const MessageBox = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.fill.warningSubtle};
`;

const EventInfo = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
  align-items: center;
  padding-bottom: ${({ theme }) => theme.sizing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
`;

const EventImage = styled.img`
  width: 80px;
  height: auto;
  aspect-ratio: 4/3;
  border-radius: ${({ theme }) => theme.radius.xs};
  object-fit: cover;
  flex-shrink: 0;
`;

const EventDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  // border-radius: ${({ theme }) => theme.radius.xs};
  position: relative;
  overflow: hidden;
  background-color: ${props => props.$isUser ? theme.fill.defaultSubtle : theme.fill.defaultMain};

  &:last-child {
    border-bottom: none;
  }
`;

const Decoration = styled.div`
  width: 2px;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.fill.defaultStrong};
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const MessageContainer = styled.div`
  display: flex;
  // justify-content: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  // margin-top: ${({ theme }) => theme.sizing.sm};
`;

const MessageWrapper = styled.div`
  max-width: 80%;
  display: flex;
  flex-direction: column;
`;

const MessageContent = styled.div`
  background-color: ${({ theme, $isUser }) => $isUser ? theme.fill.brandAlphaMain16 : theme.fill.defaultWeak};
  padding: ${({ theme }) => theme.sizing.sm};
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
`;

const DrawerFooter = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background-color: ${({ theme }) => theme.fill.defaultMain};
  flex-shrink: 0;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;
